import { useEffect, useState } from "react";
import { useSearchParams, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Printer, FileDown, Table2, Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { downloadCustomReportPdf, downloadCustomReportCsv } from "@/lib/customReportExport";
import { buildCustomReportHtml } from "@/components/reports/CustomReportBuilder";

// R1-04B targeted fix (2026-10-29): a genuinely standalone, routed page — reached via a real
// <a target="_blank"> link, never a scripted window.open() — so printing from here never depends
// on popup timing and isn't affected by any sandboxing of an embedding preview frame. Deliberately
// skips the shared `Protected`/`Layout` chrome (sidebar/nav) for a clean, print-ready document;
// does its own light owner/manager auth check instead.
export default function CustomReportPrintView() {
  const { user, business, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const cur = business?.currency || "JMD $";

  const start = params.get("start") || "";
  const end = params.get("end") || "";
  const sections = params.get("sections") || "";
  const groupBy = params.get("group_by") || "sku";
  const reqParams = { start, end, sections, group_by: groupBy };

  useEffect(() => {
    if (authLoading || !user || !["owner", "manager"].includes(user.role)) return;
    if (!start || !end || !sections) { setError("This report link is missing its period or sections — please go back and open it again from Build Custom Report."); return; }
    api.get("/reports/custom", { params: reqParams })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.status === 403 ? (e.response?.data?.detail || "You do not have permission to view this report.") : "Could not load this report — please go back and try again."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, start, end, sections, groupBy]);

  if (authLoading) return <div className="h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!["owner", "manager"].includes(user.role)) return <Navigate to="/" replace />;

  const doPdf = async () => { setBusy("pdf"); const err = await downloadCustomReportPdf(reqParams, { start, end }); if (err) toast.error(err); setBusy(""); };
  const doCsv = async () => { setBusy("csv"); const err = await downloadCustomReportCsv(reqParams, { start, end }); if (err) toast.error(err); setBusy(""); };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="custom-report-print-view">
      <div className="no-print max-w-3xl mx-auto mb-4 flex flex-wrap items-center justify-between gap-3 bg-white border rounded-lg p-4 shadow-sm">
        <div>
          <Link to="/reports" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-1" data-testid="print-view-back-link"><ArrowLeft className="w-3 h-3" />Back to Reports</Link>
          <h1 className="text-base font-semibold">Custom Report — Print View</h1>
          <p className="text-xs text-muted-foreground">Period: {start} → {end}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => window.print()} disabled={!data} data-testid="print-view-print-now-btn"><Printer className="w-4 h-4 mr-1.5" />Print Now</Button>
          <Button variant="outline" onClick={doPdf} disabled={!!busy || !data} data-testid="print-view-pdf-btn">{busy === "pdf" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}Download PDF</Button>
          <Button variant="outline" onClick={doCsv} disabled={!!busy || !data} data-testid="print-view-csv-btn">{busy === "csv" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Table2 className="w-4 h-4 mr-1.5" />}Download CSV</Button>
        </div>
      </div>

      {error && (
        <div className="no-print max-w-3xl mx-auto flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm" data-testid="print-view-error">
          <AlertCircle className="w-4 h-4 shrink-0" />{error}
        </div>
      )}
      {!data && !error && (
        <div className="no-print max-w-3xl mx-auto flex items-center gap-2 text-muted-foreground text-sm p-4" data-testid="print-view-loading">
          <Loader2 className="w-4 h-4 animate-spin" />Loading report…
        </div>
      )}
      {data && (
        <div className="max-w-3xl mx-auto bg-white border rounded-lg shadow-sm p-10 print:shadow-none print:border-0 print:p-0 print:max-w-full" data-testid="print-view-content">
          <div className="flex items-start gap-4 mb-6 pb-5 border-b-2" style={{ borderColor: "#0ea5e9" }}>
            {business?.logo_url && <img src={business.logo_url} alt="" className="h-16 w-16 object-contain border rounded-lg bg-white p-1" />}
            <div className="flex-1">
              <h2 className="text-xl font-bold">{business?.name || "NEEC POS"}</h2>
              {business?.address && <p className="text-xs text-muted-foreground mt-0.5">{business.address}{business?.parish ? `, ${business.parish}` : ""}</p>}
              {business?.phone && <p className="text-xs text-muted-foreground">Tel: {business.phone}</p>}
            </div>
            <div className="text-right">
              <h3 className="text-base font-bold uppercase tracking-wide" style={{ color: "#0ea5e9" }}>Custom Report</h3>
              <p className="text-xs text-muted-foreground mt-1">Period: {start} → {end}</p>
            </div>
          </div>
          <div className="report-body" dangerouslySetInnerHTML={{ __html: buildCustomReportHtml(data, cur) }} />
          <div className="mt-10 pt-4 border-t text-center text-[11px] text-slate-400">
            Custom Report · Period: {start} → {end} · Generated {new Date().toLocaleString()}<br />Powered by NEEC POS
          </div>
        </div>
      )}

      <style>{`
        @media print { .no-print { display: none !important } body { background: #fff !important } }
        .report-body table.data{width:100%;border-collapse:collapse;margin-top:16px;font-size:13px}
        .report-body table.data th{background:#f1f5f9;text-align:left;padding:10px;font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:#475569}
        .report-body table.data td{padding:10px;border-bottom:1px solid #e2e8f0}
        .report-body table.data td.right,.report-body table.data th.right{text-align:right}
        .report-body .section{margin-top:20px;break-inside:avoid-page}
        .report-body .section h3{font-size:12px;text-transform:uppercase;letter-spacing:.5px;color:#64748b;margin-bottom:6px}
        .report-body .notes{margin-top:10px;font-size:12px;color:#475569}
      `}</style>
    </div>
  );
}
